import { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Lock } from "lucide-react";
import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { ActionBarJumps } from "@/components/patterns/ActionBar";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useLockOnceSettled } from "@/hooks/use-claim-strategy";
import { plural } from "@/lib/plural";
import { refusalDetail } from "@/services/api-client";
import { StageFooter, landOn } from "../layout/StageFooter";
import { CardAndBanner, StepBanner } from "../notifications/StepChanges";
import { destinationAfter, stageAfter } from "../stage-vocabulary";
import {
  CHECKING_FOR_QUESTIONS,
  useQuestionGate,
} from "../questions/useQuestionGate";

const UNREACHED = "The lock did not reach the server.";

/** Where a claim's row is, for a jump from the bar. */
export function claimAnchor(claimId: string): string {
  return `claim-${claimId}`;
}

/** Where an unplaced position's row is, for a jump from the bar. */
export function unplacedAnchor(positionId: string): string {
  return `unplaced-${positionId}`;
}

/** The items the bar notes and never blocks on: placeholder claims and positions not yet placed. */
function Notes({ stage }: { stage: ClaimStrategyResponse }) {
  const jump = (anchor: string) => () => {
    landOn(anchor);
  };
  return (
    <ActionBarJumps
      jumps={[
        ...stage.claims
          .filter((claim) => claim.placeholder)
          .map((claim) => ({
            key: claim.claim_id,
            label: `Claim ${String(claim.number)} is marked as a placeholder`,
            onJump: jump(claimAnchor(claim.claim_id)),
          })),
        ...stage.unplaced.map((position) => ({
          key: position.position_id,
          label: `Position ${String(position.number)} is not placed in a claim`,
          onJump: jump(unplacedAnchor(position.position_id)),
        })),
      ]}
    />
  );
}

/** The bar's two lines in the design's words: the act it closes, and what stands before it. */
function words(stage: ClaimStrategyResponse): {
  heading: string;
  detail: string;
} {
  const { gate, claims } = stage;
  if (gate.locked) {
    return {
      heading: "The claim set is locked",
      detail: "Continue when you are ready.",
    };
  }
  const heading = "Agree claim strategy";
  if (gate.ready) {
    return {
      heading,
      detail:
        "The claim set is ready. Locking fixes it as agreed and opens drafting.",
    };
  }
  const outstanding = gate.conditions.find(
    (c) => c.outstanding && c.id !== "confirmed",
  );
  if (outstanding !== undefined) return { heading, detail: outstanding.label };
  const toConfirm = claims.filter((c) => !c.confirmed && !c.placeholder).length;
  const placeholders = claims.filter((c) => c.placeholder).length;
  const marked =
    placeholders === 0 ? "" : `, ${String(placeholders)} marked as placeholder`;
  return {
    heading,
    detail: `Confirm every independent and dependent claim to continue: ${String(toConfirm)} still to confirm${marked}.`,
  };
}

/** The way on from Claim Strategy: the stage after it, where there is one. */
function useOnward(taskId: string): () => void {
  const navigate = useNavigate();
  const next = destinationAfter(taskId, "claim-strategy");
  return () => {
    if (next !== undefined) void navigate(next);
  };
}

/**
 * Praxis asks its questions before the lock lands (lane L decision 30), and
 * ahead of the press once the set is ready to lock.
 */
function useLockQuestions(
  taskId: string,
  stage: ClaimStrategyResponse,
  lock: () => void,
) {
  return useQuestionGate({
    taskId,
    trigger: "claim_lock",
    topic: "claim strategy",
    cta: "Continue",
    commit: lock,
    ready: stage.gate.ready && !stage.gate.locked,
    reading: JSON.stringify(stage.claims),
  });
}

/**
 * The one act that closes the stage: lock the set as agreed and continue. Closed
 * until every claim not marked a placeholder is confirmed; a placeholder claim
 * and an unplaced position are noted and never block, and locking over a
 * placeholder asks first. The lock waits for a write still on its way.
 */
export function ClaimStrategyFooter({
  taskId,
  stage,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
}) {
  const { lock, lockAt, busy } = useLockOnceSettled(taskId);
  const [asking, setAsking] = useState(false);
  const onward = useOnward(taskId);
  const gate = useLockQuestions(taskId, stage, () => {
    lockAt(stage, onward);
  });
  const placeholders = stage.claims.filter((c) => c.placeholder).length;
  const { heading, detail } = words(stage);
  const locked = stage.gate.locked;
  if (gate.card !== null) {
    return (
      <CardAndBanner card={gate.card} taskId={taskId} step="claim_strategy" />
    );
  }
  return (
    <>
      <StageFooter
        testId="claim-strategy-footer"
        heading={heading}
        detail={detail}
        ready={stage.gate.ready}
        settled={locked}
        submitting={busy || gate.asking}
        label={
          locked
            ? `Continue to ${stageAfter("claim-strategy")?.label ?? "the next stage"}`
            : "Continue"
        }
        pendingLabel={gate.asking ? CHECKING_FOR_QUESTIONS : "Locking…"}
        conditions={stage.gate.conditions}
        resolveTarget={(condition) =>
          condition.first_id === null ? null : claimAnchor(condition.first_id)
        }
        onSubmit={() => {
          if (locked) onward();
          else if (placeholders > 0) setAsking(true);
          else gate.press();
        }}
        sticky
        above={<StepBanner taskId={taskId} step="claim_strategy" />}
        actionIcon={
          locked ? undefined : lock.isPending ? <ArrowRight /> : <Lock />
        }
      >
        {lock.isError && (
          <p role="alert" className="mt-2 text-[11.5px] text-destructive">
            {refusalDetail(lock.error, UNREACHED)}
          </p>
        )}
        <Notes stage={stage} />
      </StageFooter>
      <ConfirmDialog
        open={asking}
        title={`Lock with ${plural(placeholders, "placeholder claim")}?`}
        description="Each is carried forward marked as a placeholder."
        actionLabel="Lock"
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          setAsking(false);
          gate.press();
        }}
      />
    </>
  );
}
